// next-app/src/components/survey/rsvp-survey.tsx
'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSurveyConfig, useSubmitSurvey } from '@/hooks';
import { SurveyForm, PersonInfo, EmailOptIn } from '@/components/survey';
import { basicSurveySchema, type BasicSurveyForm } from '@/lib/validations/survey';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface RSVPSurveyProps {
  hash: string;
}

export function RSVPSurvey({ hash }: RSVPSurveyProps) {
  const router = useRouter();

  // RSVP does not use event_portal parameter
  const successPath = '/submit/success';
  const errorPath = '/submit/error';

  // Fetch survey configuration
  const { data: configData, isLoading, isError, error } = useSurveyConfig(hash);
  const config = configData?.config;

  // Form setup - RSVP uses basic schema
  const form = useForm<BasicSurveyForm>({
    resolver: zodResolver(basicSurveySchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      event_id: 0,
      hash: hash,
      survey_type: 'rsvp',
      email_opt_in: false,
    },
  });

  // Update form when config loads
  useEffect(() => {
    if (config) {
      form.setValue('event_id', config.event_id);
    }
  }, [config, form]);

  // Submission handler
  const submitSurvey = useSubmitSurvey(hash, {
    onSuccess: () => {
      router.push(successPath);
    },
    onError: () => {
      router.push(errorPath);
    },
  });

  const handleSubmit = async (data: BasicSurveyForm) => {
    submitSurvey.mutate({
      ...data,
      responses: {},
    });
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !config) {
    return (
      <div className="mx-auto max-w-2xl">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error?.message || 'Unable to load survey. Please check the URL and try again.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <SurveyForm
      form={form}
      onSubmit={handleSubmit}
      title="RSVP"
      description="Please RSVP for this upcoming event"
      eventName={config.event_name}
      dealerName={config.dealer_name}
      isSubmitting={submitSurvey.isPending}
      submitText="Submit RSVP"
    >
      <PersonInfo disabled={submitSurvey.isPending} />

      <hr className="my-6" />

      <EmailOptIn optInText={config.email_opt_in_text} disabled={submitSurvey.isPending} />
    </SurveyForm>
  );
}
